import axios from 'axios';
import constants from '../../config/constants';
import { EVERYONE, normalizeUsername } from '../../config/featureAccess';
import { setFeatureFlags } from './auth';

// A feature is switched on for one username by a school setting (CommonConfig) named FEATURE_X:<username>, e.g.
// FEATURE_TRANSPORT:adminobs@gmail.com = ENABLE, so it can be changed without a deploy. Which features exist is
// in config/featureAccess.js; the Feature Access page (views/admin/FeatureAccess) edits the settings. The backend
// has no delete for settings, so "off" is a setting whose value is DISABLE.
const CONFIG = `${constants.API_BASE_URL}/api/config`;

export const fetchSchoolConfigApi = async ({ schoolId, token }) => {
  const res = await axios.get(`${CONFIG}/school-config/${schoolId}`, constants.setHeader(token));
  return res.data || [];
};

export const createSchoolConfigApi = ({ postData, token }) =>
  axios.post(`${CONFIG}/create-common-config`, postData, constants.setHeader(token));

export const updateSchoolConfigApi = ({ configId, postData, token }) =>
  axios.put(`${CONFIG}/update-common-config/${configId}`, postData, constants.setHeader(token));

// Staff logins (teachers, drivers, librarians...) of the school as { email: { name, designation } }. An admin can
// read this list but not look up other logins, so it is how the Feature Access page tells a staff login from an
// admin one. Resolves to {} if it can't be read (then no staff check is made).
export const fetchEmployeeLoginsApi = async ({ schoolId, token }) => {
  try {
    const res = await axios.get(
      `${constants.API_BASE_URL}/api/admin/get-school-employees/${schoolId}`,
      constants.setHeader(token)
    );
    return Object.fromEntries(
      (res.data || [])
        .filter(employee => employee.email)
        .map(employee => [
          employee.email.trim().toLowerCase(),
          { name: employee.name, designation: employee.designation },
        ])
    );
  } catch (err) {
    return {};
  }
};

// The feature settings in a school's config, one entry per username:
// [{ configId, configName: 'FEATURE_TRANSPORT', username, configValue, row }].
export const parseFeatureSettings = configs =>
  configs
    .filter(config => (config.configName || '').startsWith('FEATURE_') && config.configName.includes(':'))
    .map(config => {
      const separator = config.configName.indexOf(':');
      return {
        configId: config.configId,
        configName: config.configName.slice(0, separator),
        username: config.configName.slice(separator + 1).toLowerCase(),
        configValue: config.configValue,
        row: config,
      };
    });

// The logged-in user's own switches, as { FEATURE_X: value }, into the store (read once per login).
export const fetchFeatureFlagsService = data => async dispatch => {
  const { schoolId, email, token } = data;
  let flags = {};
  try {
    const configs = await fetchSchoolConfigApi({ schoolId, token });
    const settings = parseFeatureSettings(configs);
    const flagsFor = username =>
      Object.fromEntries(
        settings
          .filter(setting => setting.username === username)
          .map(setting => [setting.configName, setting.configValue])
      );
    // The setting for everyone first, then the user's own on top (so it can also switch a feature off for them).
    flags = { ...flagsFor(EVERYONE), ...flagsFor(normalizeUsername(email)) };
  } catch (err) {
    // Unreadable settings mean every gated feature stays off.
    console.error('err-fetchFeatureFlagsService', err);
  }
  dispatch(setFeatureFlags(flags));
};
